import timezoneCountries from "./timezone-countries.json";
import type { SelectOption } from "@/modules/auth/services/auth.api";

/**
 * "Country / Time Zone" is one control for the user but two values for the API
 * (an ISO 3166-1 alpha-2 country and an IANA zone), and nothing in the
 * database links them. `timezone-countries.json` is generated from the IANA
 * zone table - see scripts/generate-timezone-countries.mjs - and is the join.
 */
const TIMEZONE_TO_COUNTRY = timezoneCountries as Record<string, string>;

/** The country an IANA zone belongs to, or null for a zone that is not a place. */
export function countryForTimezone(timezone: string): string | null {
  return TIMEZONE_TO_COUNTRY[timezone] ?? null;
}

/**
 * One option per timezone, labelled with its country so the single control
 * reads as "Country / Time Zone".
 *
 * Zones with no country are dropped. In practice that is only "UTC", which is
 * not a location anyone lives in - it is the column default a Google-created
 * account starts with, and the whole point of this field is to replace it.
 * Keeping it would offer an option that cannot satisfy the API's required
 * `country`.
 *
 * Offsets come from the timezone list the API already returns, so this never
 * invents a label the backend would reject.
 */
export function buildCountryTimezoneOptions(
  countries: SelectOption[],
  timezones: SelectOption[],
): SelectOption[] {
  const countryNames = new Map(countries.map((c) => [c.value.toUpperCase(), c.label]));

  return timezones
    .flatMap((timezone) => {
      const code = countryForTimezone(timezone.value);

      if (!code) {
        return [];
      }

      const countryName = countryNames.get(code.toUpperCase());

      // A zone whose country the API does not list is unusable too: the
      // backend validates `country` against its own countries table.
      if (!countryName) {
        return [];
      }

      // `timezone.label` already carries the offset the API formatted, e.g.
      // "Africa/Cairo (UTC+02:00)" - reuse it rather than recomputing.
      return [{ value: timezone.value, label: `${countryName} — ${timezone.label}` }];
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}
